import type {
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  Ref,
  UIEventHandler,
} from "react";
import { forwardRef, useContext, useMemo } from "react";

import { useUniqueId } from "metabase/common/hooks/use-unique-id";

import type { TabContextType } from "../Tab";
import { TabContext } from "../Tab";

import { TabListContent, TabListRoot } from "./TabList.styled";

export interface TabListProps<T> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  value?: T;
  onChange?: (value: T) => void;
  onScroll?: UIEventHandler<HTMLDivElement>;
  children?: ReactNode;
  controls?: ReactNode;
  itemRole?: "button" | "tab";
}

function handleCompositeKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  itemRole: "button" | "tab",
) {
  const itemSelector = `[role="${itemRole}"]`;
  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(itemSelector),
  ).filter((item) => item.parentElement?.closest(itemSelector) === null);
  if (items.length === 0) {
    return;
  }

  const currentIndex = items.indexOf(document.activeElement as HTMLElement);
  if (currentIndex === -1) {
    return;
  }

  let nextIndex: number | null = null;
  switch (event.key) {
    case "ArrowRight":
      nextIndex = (currentIndex + 1) % items.length;
      break;
    case "ArrowLeft":
      nextIndex = (currentIndex - 1 + items.length) % items.length;
      break;
    case "Home":
      nextIndex = 0;
      break;
    case "End":
      nextIndex = items.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const nextItem = items[nextIndex];
  nextItem.focus();
  if (itemRole === "tab") {
    nextItem.click();
  }
}

const TabListInner = forwardRef(function TabGroup<T>(
  {
    value,
    onChange,
    onScroll,
    children,
    controls,
    itemRole = "tab",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...props
  }: TabListProps<T>,
  ref: Ref<HTMLDivElement>,
) {
  const idPrefix = useUniqueId();
  const outerContext = useContext(TabContext);

  const innerContext = useMemo(() => {
    return { value, idPrefix, onChange };
  }, [value, idPrefix, onChange]);

  const activeContext = outerContext.isDefault ? innerContext : outerContext;

  return (
    <TabListRoot {...props}>
      <TabListContent
        ref={ref}
        role={itemRole === "tab" ? "tablist" : "toolbar"}
        aria-orientation={itemRole === "button" ? "horizontal" : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onScroll={onScroll}
        onKeyDown={(event) => handleCompositeKeyDown(event, itemRole)}
      >
        <TabContext.Provider value={activeContext as TabContextType}>
          {children}
        </TabContext.Provider>
      </TabListContent>
      {controls}
    </TabListRoot>
  );
});

export const TabList = Object.assign(TabListInner, {
  Root: TabListRoot,
  Content: TabListContent,
});
